import { supabase } from '../supabaseClient';
import {
  noteSupabaseFailure,
  noteSupabaseSuccess,
  isSupabaseCircuitError,
  shouldShortCircuitSupabase,
  supabaseCircuitError,
} from './supabaseCircuitBreaker';

const PRO_DATA_CACHE_PREFIX = 'ss_pro_detail_cache_v4:';
const PRO_DATA_CACHE_TTL_MS = 15 * 60 * 1000;
const THIN_PRO_DATA_CACHE_TTL_MS = 60 * 1000;
const DEFAULT_TIMEOUT_MS = 10000;
const DEFAULT_RETRY_TIMEOUT_MS = 15000;
const PRO_TIMEOUT_MESSAGE = 'Pro data request timed out';
const AUTH_LOCK_MESSAGE = 'Temporary auth session conflict';

function nowMs() {
  return Date.now();
}

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storageAvailable() {
  return typeof window !== 'undefined' && window.localStorage;
}

function storageKey(cacheKey) {
  return `${PRO_DATA_CACHE_PREFIX}${cacheKey}`;
}

function isFresh(entry) {
  const ttl = Number.isFinite(entry?.ttl_ms) ? entry.ttl_ms : PRO_DATA_CACHE_TTL_MS;
  return entry?.data && Number.isFinite(entry.cached_at) && (nowMs() - entry.cached_at) < ttl;
}

function isRichProPayload(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.pro_snapshot_missing) return false;
  if (data.snapshot_source === 'precomputed') return true;
  return (
    (Array.isArray(data.supply_chain) && data.supply_chain.length > 0) ||
    (Array.isArray(data.trusted_parties) && data.trusted_parties.length > 0) ||
    Boolean(data.pdf_extracted_at || data.extraction_confidence)
  );
}

class ProDataCache {
  constructor() {
    this.memory = new Map();
  }

  get(cacheKey) {
    const memoryEntry = this.memory.get(cacheKey);
    if (isFresh(memoryEntry)) return memoryEntry.data;
    if (memoryEntry) this.memory.delete(cacheKey);

    if (!storageAvailable()) return null;
    const persisted = safeParse(window.localStorage.getItem(storageKey(cacheKey)));
    if (!isFresh(persisted)) {
      if (persisted) window.localStorage.removeItem(storageKey(cacheKey));
      return null;
    }

    this.memory.set(cacheKey, persisted);
    return persisted.data;
  }

  set(cacheKey, data) {
    const isRich = isRichProPayload(data);
    const entry = {
      cached_at: nowMs(),
      ttl_ms: isRich ? PRO_DATA_CACHE_TTL_MS : THIN_PRO_DATA_CACHE_TTL_MS,
      data,
    };
    this.memory.set(cacheKey, entry);

    if (!isRich || !storageAvailable()) return this;
    try {
      window.localStorage.setItem(storageKey(cacheKey), JSON.stringify(entry));
    } catch {
      // Local storage is best-effort. Keep the in-memory cache and continue.
    }
    return this;
  }

  has(cacheKey) {
    return Boolean(this.get(cacheKey));
  }

  delete(cacheKey) {
    this.memory.delete(cacheKey);
    if (storageAvailable()) {
      try {
        window.localStorage.removeItem(storageKey(cacheKey));
      } catch {
        // Best-effort only.
      }
    }
    return true;
  }

  clear() {
    this.memory.clear();
    if (!storageAvailable()) return;
    try {
      Object.keys(window.localStorage)
        .filter(key => key.startsWith(PRO_DATA_CACHE_PREFIX))
        .forEach(key => window.localStorage.removeItem(key));
    } catch {
      // Best-effort only.
    }
  }
}

// Persistent cache for PWA/app resumes, backed by memory for instant repeats.
export const proDataCache = new ProDataCache();
const inFlightProRequests = new Map();

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isSupabaseAuthLockError(error) {
  const message = String(error?.message || error || '').toLowerCase();
  return message.includes('auth-token') && (
    message.includes('lock:') ||
    message.includes('locks:') ||
    message.includes('another request stole it') ||
    message.includes('was released')
  );
}

function isTransientAuthConflict(error) {
  const message = String(error?.message || error || '').toLowerCase();
  return isSupabaseAuthLockError(error) || message.includes(AUTH_LOCK_MESSAGE.toLowerCase());
}

function publicProError(error) {
  if (!isSupabaseAuthLockError(error)) return error;
  return new Error(AUTH_LOCK_MESSAGE);
}

async function withAuthLockRetry(fn, { attempts = 3, baseDelayMs = 180 } = {}) {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (!isSupabaseAuthLockError(error) || attempt >= attempts - 1) break;
      await delay(baseDelayMs * (attempt + 1));
    }
  }
  throw publicProError(lastError);
}

async function requireActiveSession({ forceRefresh = false } = {}) {
  if (forceRefresh && typeof supabase.auth.refreshSession === 'function') {
    const { data, error } = await withAuthLockRetry(() => supabase.auth.refreshSession());
    if (!error && data?.session?.access_token) return data.session;
    console.warn('[ProDetail] Session refresh before Pro fetch failed:', error?.message || error);
  }

  const { data, error } = await withAuthLockRetry(() => supabase.auth.getSession());
  if (error) throw new Error(error.message || 'Could not read auth session');
  if (!data?.session?.access_token) throw new Error('Authentication required');
  return data.session;
}

function withTimeout(promiseFactory, timeoutMs) {
  const controller = new AbortController();
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      reject(new Error(PRO_TIMEOUT_MESSAGE));
    }, timeoutMs);
  });

  return Promise.race([promiseFactory(controller.signal), timeoutPromise])
    .catch(error => {
      if (error.name === 'AbortError') {
        throw new Error(PRO_TIMEOUT_MESSAGE);
      }
      throw error;
    })
    .finally(() => clearTimeout(timeoutId));
}

function isLocalProTimeout(error) {
  return String(error?.message || error || '') === PRO_TIMEOUT_MESSAGE;
}

function shouldRefreshAndRetry(error) {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    isTransientAuthConflict(error) ||
    message.includes('authentication required') ||
    message.includes('jwt') ||
    message.includes('session') ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('load failed')
  );
}

/**
 * Fetches Pro Intelligence data for a DA via the get_da_detail RPC.
 * Returns cached data immediately on repeat calls.
 * Throws on RPC error or timeout. The caller is responsible for cancelled-flag checks.
 *
 * @param {string} daId      - UUID of the development_application
 * @param {{ timeoutMs?: number, retryTimeoutMs?: number, cacheScope?: string, bypassCache?: boolean, forceRefreshSession?: boolean, recordCircuitFailure?: boolean }} options
 * @returns {Promise<object>} Resolved RPC data
 */
export async function fetchDAProDetail(
  daId,
  {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retryTimeoutMs = DEFAULT_RETRY_TIMEOUT_MS,
    cacheScope = 'user',
    bypassCache = false,
    forceRefreshSession = false,
    recordCircuitFailure = true,
  } = {}
) {
  const cacheKey = `${cacheScope}:${daId}`;
  const cached = !bypassCache ? proDataCache.get(cacheKey) : null;
  if (cached) {
    return cached;
  }

  if (shouldShortCircuitSupabase()) {
    throw supabaseCircuitError();
  }

  if (!bypassCache && inFlightProRequests.has(cacheKey)) {
    return inFlightProRequests.get(cacheKey);
  }

  const runRequest = async (signal, { refreshSession = false } = {}) => {
    await requireActiveSession({ forceRefresh: refreshSession });
    let { data, error } = await withAuthLockRetry(() => supabase
      .rpc('get_da_detail', { da_id: daId })
      .abortSignal(signal));

    if (error && /authentication required|jwt|session/i.test(error.message || '')) {
      await requireActiveSession({ forceRefresh: true });
      ({ data, error } = await withAuthLockRetry(() => supabase
        .rpc('get_da_detail', { da_id: daId })
        .abortSignal(signal)));
    }

    if (error) throw new Error(error.message || 'Pro data request failed');
    if (data?.snapshot_source !== 'precomputed') return data;

    // The reviewed PDF report is an additive, fail-soft Pro surface. It is
    // served by a separate entitlement-checked RPC so the private shadow
    // schema is never exposed to the browser.
    try {
      const { data: pdfReport, error: pdfError } = await withAuthLockRetry(() => supabase
        .rpc('get_da_pdf_intelligence_report', { p_da_id: daId })
        .abortSignal(signal));
      if (pdfError) throw new Error(pdfError.message || 'Document intelligence request failed');
      if (pdfReport?.available === true && Array.isArray(pdfReport.facts) && pdfReport.facts.length > 0) {
        return { ...data, pdf_intelligence_report: pdfReport };
      }
    } catch (pdfError) {
      console.warn('[ProDetail] Reviewed document intelligence unavailable:', pdfError?.message || pdfError);
    }
    return data;
  };

  const request = (async () => {
    let result;
    try {
      result = await withTimeout(
        signal => runRequest(signal, { refreshSession: forceRefreshSession }),
        timeoutMs
      );
    } catch (firstError) {
      if (isSupabaseCircuitError(firstError)) {
        throw firstError;
      }
      if (recordCircuitFailure && !isLocalProTimeout(firstError) && !isTransientAuthConflict(firstError)) {
        noteSupabaseFailure(firstError);
      }
      proDataCache.delete(cacheKey);
      if (!shouldRefreshAndRetry(firstError)) {
        throw firstError;
      }

      // Stale Safari/PWA tabs can keep an old auth/client state until a full reload.
      // One forced session refresh plus a clean RPC retry usually unwinds that state.
      result = await withTimeout(
        signal => runRequest(signal, { refreshSession: !isTransientAuthConflict(firstError) }),
        Math.max(timeoutMs, retryTimeoutMs)
      ).catch(error => {
        throw publicProError(error);
      });
    }

    noteSupabaseSuccess();
    proDataCache.set(cacheKey, result);
    return result;
  })();

  inFlightProRequests.set(cacheKey, request);
  try {
    return await request;
  } finally {
    if (inFlightProRequests.get(cacheKey) === request) {
      inFlightProRequests.delete(cacheKey);
    }
  }
}
